import { Col, Container, Row } from 'react-bootstrap';
import { Dispatch, SetStateAction } from 'react';
import { SortDirectionEnum } from '@/models/gen/graphql';
import { TODAY, TODAY_EOD } from '@/constants';
import { refetchIncidentIssueWithDocuments } from './api/services/searchIncidentIssue';

import Feature from '@/common/Feature';
import IncidentHistoryFilters from '@/features/IncidentHistory/components/IncidentHistoryFilters';
import IncidentHistoryTable from '@/features/IncidentHistory/components/IncidentHistoryTable';
import { IncidentIssueWithDocuments } from './components/IncidentHistoryForm/types';
import { useProvider } from '@/common/Provider';
import { ServiceIssueFiltersType } from '@/hooks/useServiceIssueFilters';

const IncidentHistory = (): JSX.Element => {
  return (
    <Feature name="IncidentHistory" context={initIncidentHistoryState} methods={incidentHistoryMethodConstructor}>
      <IncidentHistoryFilters />
      <Container>
        <Row>
          <Col>
            <IncidentHistoryTable />
          </Col>
        </Row>
      </Container>
    </Feature>
  );
};

export type IncidentHistoryFiltersType = {
  startDatetime: string;
  endDatetime: string;
  airportCode: string[];
  tripServicerIataAirlineCodes: string[];
};
export type IncidentHistoryRow = IncidentIssueWithDocuments;
export type IncidentHistoryContext = {
  filters: ServiceIssueFiltersType;
  incidents: Map<string, IncidentHistoryRow>;
  totalIncidents: number;
  selected: Map<string, IncidentHistoryRow>;
  loading: boolean;
  sortBy: string;
  sortDirection: SortDirectionEnum;
  search: string;
};
export type IncidentHistoryMethods = {
  resetState: (overrides?: Partial<IncidentHistoryContext>) => void;
  submitFilters: (values?: Partial<IncidentHistoryFiltersType>) => Promise<void>;
};

export const initIncidentHistoryState: IncidentHistoryContext = {
  filters: {
    startDatetime: TODAY,
    endDatetime: TODAY_EOD,
    airportCode: [],
    tripServicerIataAirlineCodes: [],
  },
  incidents: new Map(),
  totalIncidents: 0,
  selected: new Map(),
  sortBy: '',
  sortDirection: SortDirectionEnum.Asc,
  loading: false,
  search: '',
};
export const incidentHistoryMethodConstructor = (
  state: IncidentHistoryContext,
  setState: Dispatch<SetStateAction<IncidentHistoryContext>>
): IncidentHistoryMethods => ({
  resetState: (overrides?: Partial<IncidentHistoryContext>): void => setState({ ...initIncidentHistoryState, ...(overrides || {}) }),
  submitFilters: async (values: ServiceIssueFiltersType = state.filters): Promise<void> => {
    try {
      setState((prevState) => ({ ...prevState, loading: true }));
      const res = await refetchIncidentIssueWithDocuments([values]);
      console.log('IncidentHistory submitFilters', res);
      setState((current) => {
        const incidents = new Map<string, IncidentHistoryRow>(res.rows.map((issue) => [issue.id, issue]));
        const totalIncidents = res.totalCount;
        return { ...current, incidents, totalIncidents };
      });
    } catch (err) {
      console.error('Error submitting filters:', err);
    } finally {
      setState((prevState) => ({ ...prevState, loading: false }));
    }
  },
});
export function useIncidentHistory<T = IncidentHistoryContext>(
  selector?: (state: IncidentHistoryContext) => T
): [T, Dispatch<SetStateAction<IncidentHistoryContext>>, IncidentHistoryMethods] {
  return useProvider<IncidentHistoryContext, T, IncidentHistoryMethods>(selector);
}

export default IncidentHistory;
